import React, { useEffect, useRef, useState } from "react";
import DashboardContent from "../../components/DashboardContent";
import useModalWindow from "../../hooks/useModalWindow";
import useCsr from "../../hooks/useCsr";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { CsrAPI } from "../../apis/CsrAPI";
import * as Sentry from "@sentry/react";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";
import RedirectHandler from "../../components/RedirectHandler";
import StudySelectDropdown from "../../components/StudySelectDropdown";

const SelectStudy = () => {
  const { studyId } = useParams();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const csr_study_id = queryParams.get("csr_study_id");

  const { modalWindow } = useModalWindow();
  const {
    setAjaxLoading,
    currentStudyName,
    currentLaySummaryName,
    wrongEnvironment,
    setCurrentLaySummaryName,
    selectedStudy,
    setSelectedStudy,
  } = useCsr();
  const navigate = useNavigate();

  const studyNameRef = useRef();
  const errRef = useRef();

  const [laySummaryName, setLaySummaryName] = useState("");
  const [selectedStudyID, setSelectedStudyID] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [charLimitError, setCharLimitError] = useState("");

  const [showRedirect, setShowRedirect] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (currentLaySummaryName) {
      setLaySummaryName(currentLaySummaryName);
    }
    // When currentStudyName is available (from Dashboard navigation), set it as selectedStudy
    if (currentStudyName && !selectedStudy) {
      setSelectedStudy(currentStudyName);
    }
  }, [currentStudyName, currentLaySummaryName, selectedStudy]);

  useEffect(() => {
    if (!errMsg) {
      studyNameRef.current?.focus();
    }
  }, [errMsg]);

  useEffect(() => {
    setErrMsg("");
  }, [studyId]);

  const handleStudyNameChange = event => {
    const value = event.target.value;
    // Check character limit and show error if exceeded
    if (value.length > 200) {
      setCharLimitError("Lay Summary Name cannot exceed 200 characters");
    } else {
      setCharLimitError("");
      setLaySummaryName(value);
    }
  };

  const handleStudySelection = selectedStudyValue => {
    setSelectedStudy(selectedStudyValue.value);
    setSelectedStudyID(selectedStudyValue.studyId);
    // Update study name when a study is selected
    if (selectedStudyValue.value) {
      setLaySummaryName(selectedStudyValue.value + " - Lay Summary");
    }
  };

  useEffect(() => {
    setErrMsg("");
    setCharLimitError("");
  }, [laySummaryName]);

  const handleSummary = async event => {
    event.preventDefault();

    try {
      const isConfirmed = await modalWindow({
        title: "Note to the writer",
        message:
          "You will be asked several questions about the study. Please be prepared by having available a copy of the CSR and synopsis (for your information). Be aware, as this is a lay summary, that wording will be changed from the original CSR to a format more applicable to a lay audience.",
        confirmButtonLabel: "OK",
      });

      if (isConfirmed) {
        setAjaxLoading(true);

        try {
          const laySummaryName = studyNameRef.current?.value;
          setCurrentLaySummaryName(laySummaryName);
          const selectedStudyIDValue = selectedStudyID
            ? selectedStudyID
            : csr_study_id;
          const response = await CsrAPI.createLaySummary({
            laySummaryName,
            selectedStudyIDValue,
            selectedStudy,
          });
          setErrMsg("");
          const newStudyId = response?.lay_summary_id;

          if (mountedRef.current) {
            if (mountedRef.current) {
              navigate("/ls/" + newStudyId + "/lay-summary-questions");
            }
          }
        } catch (error) {
          setAjaxLoading(false);
          if (mountedRef.current) {
            if (isProduction) {
              Sentry.captureException(error);
            }
            if (!error?.response) {
              setErrMsg("No Server Response");
            } else if (error.response?.status === 401) {
              setErrMsg("Invalid user credentials");
            } else if (error.response?.status === 403) {
              setShowRedirect(true);
            } else {
              setErrMsg("Fail to create a Lay Summary");
              // setSelectedStudy(selectedStudy);
            }
            errRef.current?.focus();
          }
        } finally {
          if (mountedRef.current) {
            setAjaxLoading(false);
          }
        }
      }
    } catch (modalError) {
      if (mountedRef.current) {
        setErrMsg("An unexpected error occurred");
        errRef.current?.focus();
      }
    }
  };

  const PageContent = () => {
    return (
      <div className={"csr-summary csr-summary__wrapper"}>
        <form id={"ls-summary-form"} onSubmit={handleSummary}>
          <span
            ref={errRef}
            className={errMsg ? "errmsg" : "offscreen"}
            aria-live="assertive">
            {errMsg}
          </span>
          {/* StudySelectDropdown */}
          <div className={"form-component csr-summary__form-component"}>
            <label htmlFor="study-select">Select a Clinical Study Report</label>
            {currentStudyName || studyId ? (
              <StudySelectDropdown
                selectedValue={selectedStudy}
                // onChange={handleStudySelection}
                placeholder="Select a study..."
                className="study-select"
                isDisabled={true}
              />
            ) : (
              <StudySelectDropdown
                selectedValue={selectedStudy}
                onChange={handleStudySelection}
                placeholder="Select a study..."
                className="study-select"
              />
            )}
          </div>
          <div className={"form-component csr-summary__form-component"}>
            <label htmlFor="studyname">Lay Summary Name</label>
            {/* {currentStudyName || studyId ? ( */}
            {studyId ? (
              <input
                type="text"
                id="studyname"
                ref={studyNameRef}
                autoComplete="off"
                value={laySummaryName}
                required
                aria-invalid={laySummaryName ? "false" : "true"}
                placeholder={"Enter a Lay Summary Name"}
                className={"input-text"}
                disabled={true}
              />
            ) : (
              <>
                <input
                  type="text"
                  id="studyname"
                  ref={studyNameRef}
                  autoComplete="off"
                  onChange={handleStudyNameChange}
                  value={laySummaryName}
                  required
                  aria-invalid={
                    charLimitError ? "true" : laySummaryName ? "false" : "true"
                  }
                  placeholder={"Enter a Lay Summary Name"}
                  className={"input-text"}
                />
                {charLimitError && (
                  <div
                    className="errmsg"
                    style={{ color: "red", marginTop: "5px" }}>
                    {charLimitError}
                  </div>
                )}
              </>
            )}
          </div>
        </form>
      </div>
    );
  };

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Lay Summary Report Name"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={false}
      nextButton={{
        buttonTitle: "Wizard Questions",
        buttonFormAction: studyId ? "" : "ls-summary-form",
        buttonGoToAction: studyId
          ? "/ls/" + studyId + "/lay-summary-questions"
          : "",
      }}
    />
  );
};

export default SelectStudy;
